const _ = require("lodash");
const Common = require("./CommonHelpers");

/**
 * Draws a waterfall puzzle with the user-provided puzzleData. This is a puzzle
 * type meant to teach splitting and grouping (e.g. 360=300+60), see the BaTeX
 * guide for more information.
 *
 * @param {Object} ctx canvas context
 * @param {string[][]} puzzleData array of array of strings defining the puzzle.
 *   Each inner array corresponds to one row of the puzzle, from top to bottom.
 *   Each string represents the contents of a circle, and can be a number, letter,
 *   or "[number],[letter]" comma-separated pair. The number represents a given
 *   number in the puzzle, and not providing a number gives a blank circle.
 *   The letter can be "s" or "m", depending on whether the circle splits into
 *   two circles, or is merged with another circle in the next row, respectively.
 *   The user needs to ensure certain properties, such as circles cannot be both
 *   part of a split and a merge, and that splits or merges should increase or
 *   decrease the size of the next row by 1, respectively.
 * @param {Object} options additional options
 * @param {number} [options.x=0]
 * @param {number} [options.y=0] x and y are pixel offsets for drawing the puzzle
 * @param {int[][]} blankLengths length of the blank in each circle, corresponding
 *   to the same array as puzzleData. Ignores values whose corresponding circles
 *   don't have a blank in them.
 */
const drawWaterfallPuzzle = function(ctx, puzzleData, options = {}) {
	// Diameter of individual circles.
	const DIAMETER = 50;
	// Distance between consecutive rows.
	const ROW_HEIGHT = 60;
	// Extra padding between consecutive circles in a row.
	const CIRC_PADDING = 14;
	// Height and color of the horizontal rectangles drawn behind the circles.
	const RECT_HEIGHT = 16;
	const RECT_COLOR = "#444444";
	// Stroke width of the circles.
	const STROKE_WIDTH = 2;

	const diagramSize = ctx.getSize();
	const x = options.x || 0;
	const y = options.y || 0;
	const w = diagramSize.width;
	// Array of array of integers representing the number in each circle.
	// nulls correspond to blanks.
	const numbers = [];
	// Array of array of strings. Can be "s", "m", or an empty string depending
	// on whether the circle splits into two, merges into the next row, or neither.
	const splitMerges = [];
	for (const rowData of puzzleData) {
		const numbersRow = [];
		const splitMergeRow = [];
		for (let circleData of rowData) {
			if (circleData === null || circleData.length === 0) {
				numbersRow.push(null);
				splitMergeRow.push("");
				continue;
			}

			let number = null;
			let splitOrMerge = "";
			circleData = circleData.trim().toLowerCase();
			if (circleData.includes(",")) {
				const arr = circleData.split(",");
				number = parseInt(arr[0]);
				splitOrMerge = arr[1];
			} else if (circleData === "s" || circleData === "m") {
				splitOrMerge = circleData;
			} else {
				number = parseInt(circleData);
			}

			numbersRow.push(number);
			// If two circles merge into one, then for convenience the
			// user-inputted strings will both contain "m", but it's simpler for
			// us to only record the "m" in the left circle, so that there's one
			// "m" per merge. So, we ignore every other "m" in the input.
			if (splitOrMerge === "m" && _.last(splitMergeRow) === "m") {
				splitMergeRow.push("");
			} else {
				splitMergeRow.push(splitOrMerge);
			}
		}
		numbers.push(numbersRow);
		splitMerges.push(splitMergeRow);
	}
	// Offset that each row should be drawn at. For example, the first row is
	// set to 0 by default, and if there's a split in the first row, then the
	// second row will have offset -1/2 because it's one circle longer but
	// still needs to be centered. We check whether a split or merge happens
	// first in each row, and correspondingly decrease or increase the offset by 1/2.
	const rowOffsets = [0];
	for (let i = 0; i < puzzleData.length - 1; i++) {
		let currOffset = rowOffsets[i];
		for (const s of splitMerges[i]) {
			if (s === "s") {
				currOffset -= 1 / 2;
				break;
			} else if (s === "m") {
				currOffset += 1 / 2;
				break;
			}
		}
		rowOffsets.push(currOffset);
	}
	// getPrevRowIndex takes in a row number and an index of a circle in that
	// row, and returns the index of the circle in the previous row that points
	// to the given circle (in the case of a merge, it returns the leftmost of
	// the two merged circles).
	const getPrevRowIndex = function(currRow, index) {
		let prevRowIndex = 0;
		let currRowIndex = 0;
		for (const splitOrMerge of splitMerges[currRow - 1]) {
			if (splitOrMerge === "s") {
				currRowIndex++;
			}
			if (currRowIndex >= index) {
				return prevRowIndex;
			}
			prevRowIndex++;
			currRowIndex++;
			if (splitOrMerge === "m") {
				prevRowIndex++;
			}
		}
		return prevRowIndex;
	};

	// Offsets of individual circles per row. The first circle in row i will
	// have the same offset as rowOffsets[i], and each subsequent circle will
	// be shifted forward by 1, or 2 if there are two consecutive circles that
	// split or two consecutive circles that are merged into. This is to space
	// out the circles more nicely so that e.g. in a split there is one arrow
	// pointing left and one pointing right. Note that this doesn't cover all
	// possibilities, especially if there are multiple merges/splits in consecutive
	// rows, but we expect the puzzles to be simple enough for this to handle.
	const circleOffsets = [];
	// Smallest and largest offsets over all circles, used for centering the
	// drawing on the canvas.
	let smallestOffset = 0;
	let largestOffset = 0;
	for (let i = 0; i < puzzleData.length; i++) {
		const rowData = splitMerges[i];
		let currentOffset = rowOffsets[i];
		if (currentOffset < smallestOffset) {
			smallestOffset = currentOffset;
		}
		const circleOffsetsRow = [currentOffset];
		for (let j = 1; j < rowData.length; j++) {
			const isSplit = rowData[j] === "s";
			const isPrevSplit = j > 0 && rowData[j - 1] === "s";
			const isMerged =
				i > 0 && splitMerges[i - 1][getPrevRowIndex(i, j)] === "m";
			const isPrevMerged =
				j > 0 && i > 0 && splitMerges[i - 1][getPrevRowIndex(i, j - 1)] === "m";
			if ((isSplit && isPrevSplit) || (isMerged && isPrevMerged)) {
				currentOffset += 2;
			} else {
				currentOffset += 1;
			}
			if (currentOffset > largestOffset) {
				largestOffset = currentOffset;
			}
			circleOffsetsRow.push(currentOffset);
		}
		circleOffsets.push(circleOffsetsRow);
	}

	ctx.save();
	const totalWidth =
		(largestOffset - smallestOffset + 1) * DIAMETER +
		(largestOffset - smallestOffset) * CIRC_PADDING +
		2 * STROKE_WIDTH;
	// This centers the puzzle at the top of the canvas. In particular, we
	// perform four translations:
	// 1. Shift everything to the right so that the smallest offset becomes 0.
	ctx.translate(-smallestOffset * (DIAMETER + CIRC_PADDING), 0);
	// 2. Shift right and down to account for the fact that circles are being
	// drawn at their center. After this, the puzzle is being drawn so it
	// exactly touches the top-left of the canvas.
	ctx.translate(DIAMETER / 2 + STROKE_WIDTH, DIAMETER / 2 + STROKE_WIDTH);
	// 3. Shift right to center the puzzle horizontally.
	ctx.translate((w - totalWidth) / 2, 0);
	// 4. Shift by the user-provided offsets x, y.
	ctx.translate(x, y);
	// Convert circle offsets to coordinates.
	const circleCoordinates = [];
	for (let i = 0; i < circleOffsets.length; i++) {
		const circleOffsetsRow = circleOffsets[i];
		const circleCoordinatesRow = [];
		for (let j = 0; j < circleOffsetsRow.length; j++) {
			const offset = circleOffsetsRow[j];
			const circleX = offset * (DIAMETER + CIRC_PADDING);
			const circleY = ROW_HEIGHT * i;
			circleCoordinatesRow.push({x: circleX, y: circleY});
		}
		circleCoordinates.push(circleCoordinatesRow);
	}
	// Draw background solid rectangles in each row.
	for (const circleCoordinatesRow of circleCoordinates) {
		const startCirc = circleCoordinatesRow[0];
		const endCirc = _.last(circleCoordinatesRow);
		ctx.save();
		ctx.beginPath();
		ctx.rect(
			startCirc.x,
			startCirc.y - RECT_HEIGHT / 2,
			endCirc.x - startCirc.x,
			RECT_HEIGHT
		);
		ctx.fillStyle = RECT_COLOR;
		ctx.fill();
		ctx.restore();
	}
	// Draw arrows between circles.
	const drawArrow = function(coord1, coord2) {
		ctx.save();
		// The end of the arrow should be right before the edge of the second
		// circle, do some trig to compute this point.
		const angle = Math.atan2(coord1.y - coord2.y, coord1.x - coord2.x);
		const endX = coord2.x + (Math.cos(angle) * DIAMETER) / 2;
		const endY = coord2.y + (Math.sin(angle) * DIAMETER) / 2;
		ctx.font = "14px Roboto";
		Common.drawArrow(ctx, coord1.x, coord1.y, endX, endY);
		ctx.restore();
	};
	for (let i = 1; i < circleCoordinates.length; i++) {
		const coordRow = circleCoordinates[i];
		for (let j = 0; j < coordRow.length; j++) {
			const prevCircle = getPrevRowIndex(i, j);
			drawArrow(circleCoordinates[i - 1][prevCircle], coordRow[j]);
			if (splitMerges[i - 1][prevCircle] === "m") {
				// If the previous row's circle was a merge, then the circle to
				// the right must also point to this circle.
				drawArrow(circleCoordinates[i - 1][prevCircle + 1], coordRow[j]);
			}
		}
	}
	// Draw plus signs between each pair of circles.
	for (const circleCoordinatesRow of circleCoordinates) {
		for (let i = 0; i < circleCoordinatesRow.length - 1; i++) {
			const leftCirc = circleCoordinatesRow[i];
			const rightCirc = circleCoordinatesRow[i + 1];
			ctx.fillStyle = "white";
			Common.drawBatexAtCenter(
				ctx,
				"$+$",
				(leftCirc.x + rightCirc.x) / 2,
				(leftCirc.y + rightCirc.y) / 2
			);
		}
	}
	// Draw the circles themselves and their contents.
	for (let i = 0; i < circleCoordinates.length; i++) {
		const circleCoordinatesRow = circleCoordinates[i];
		for (let j = 0; j < circleCoordinatesRow.length; j++) {
			const coord = circleCoordinatesRow[j];
			ctx.save();
			ctx.beginPath();
			ctx.arc(coord.x, coord.y, DIAMETER / 2, 0, 2 * Math.PI, false);
			ctx.lineWidth = STROKE_WIDTH;
			ctx.stroke();
			ctx.fill("white");
			let blankLength = 3;
			if (!_.isNil(options.blankLengths)) {
				blankLength = options.blankLengths[i][j];
			}
			const blankBatex = `$\\blank{${blankLength}}$`;
			const batex =
				numbers[i][j] == null ? blankBatex : numbers[i][j].toString();
			ctx.fillStyle = "black";
			Common.drawBatexAtCenter(ctx, batex, coord.x, coord.y);
			ctx.restore();
		}
	}

	ctx.restore();
};

module.exports = drawWaterfallPuzzle;
